import { useEffect } from 'react';
import {
  ArrowPathIcon,
  CalculatorIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MapPinIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import type { LegendSection } from '../IconLegendModal';
import { useIconLegend } from '../../contexts/IconLegendContext';

const iconClass = 'h-6 w-6 text-sky-600 dark:text-sky-400';

// Keep this guide aligned with etaProjection.ts, routeEtaCascade.ts,
// samsara_route_eta_cache.cascade_stop_minutes, and the DSM backend GPS math.
// These are explanations only; no separate calculation engine lives here.
const sections: LegendSection[] = [
  {
    title: 'Reading the times on a move card',
    items: [
      {
        icon: <ClockIcon className={iconClass} aria-hidden="true" />,
        title: 'ETA — the original GWI360 estimate',
        description: 'Uses GPS travel estimates and the driver’s dispatch sequence. “to Orig” means arrival at pickup; “to Dest” means arrival at delivery. Active moves show destination ETA. Queued moves show origin ETA, except linked legs, which show destination ETA. Active moves are calculated first, then queued moves in dispatch priority order.',
      },
      {
        icon: <TruckIcon className={iconClass} aria-hidden="true" />,
        title: 'Samsara Route vs. Route projection',
        description: '“Samsara Route” is the destination ETA returned by Samsara for a tracked move. “Route projection (orig/dest)” is GWI360’s estimate for a later move, starting from a current Samsara arrival and adding stop and travel time. These comparisons do not replace the original ETA or its risk colors. All are arrival estimates; arrival does not mean unloading is finished or the driver is available.',
      },
    ],
  },
  {
    title: 'Original ETA calculation',
    items: [
      {
        icon: <TruckIcon className={iconClass} aria-hidden="true" />,
        title: 'Active move — Samsara Live Share',
        description: 'The truck icon in the Pro drawer opens the active move’s Samsara Live Share page in a new tab. Samsara displays its live location and ETA there. GWI360 keeps its existing ETA calculation; the share-link job does not request Google traffic ETAs. Sync checks links every five minutes. Links expire after 24 hours and are revoked when the move ends or its assignment changes.',
      },
      {
        icon: <CalculatorIcon className={iconClass} aria-hidden="true" />,
        title: 'GPS-based travel minutes',
        description: 'Minutes = distance in miles ÷ max(current speed, 45 mph) × 60 × 1.15. Distance is the straight-line distance between GPS coordinates; 1.15 adds 15% to the travel time. Example: 30 miles at 60 mph → 30 ÷ 60 × 60 × 1.15 = 34.5 minutes. Speeds below 45 mph, including a stopped truck, use the 45 mph floor.',
      },
      {
        icon: <MapPinIcon className={iconClass} aria-hidden="true" />,
        title: 'Travel sources and fallbacks',
        description: 'For an active move, a usable GPS estimate takes priority. Other moves generally use a saved route travel duration, then available GPS estimates, then historical travel time (60 minutes if unavailable). Travel between moves uses saved location-pair travel time or a GPS-distance estimate. If that travel is still missing, the original ETA cascade uses 30 minutes. Multi-day pickup moves can target the origin appointment instead of the delivery.',
      },
      {
        icon: <LinkIcon className={iconClass} aria-hidden="true" />,
        title: 'How queued ETAs accumulate',
        description: 'Destination ETA = current time + cumulative estimated move minutes + travel between earlier moves. The next origin follows the previous destination plus any travel between locations; linked legs share the same location arrival. The original ETA cascade does not add the Samsara projection’s location dwell or 90/45-minute stop defaults. This is one reason the two estimates can differ.',
      },
    ],
  },
  {
    title: 'Samsara projection calculation',
    items: [
      {
        icon: <CalculatorIcon className={iconClass} aria-hidden="true" />,
        title: 'Arrival, stop time, then travel',
        description: 'Next origin arrival = previous destination arrival + stop time + travel to the next origin. Next destination arrival = previous destination arrival + stop time + travel to the next origin + next move travel time. Start with the active move’s fresh Samsara ETA, then repeat through queued moves. Move travel uses the saved route duration when available, otherwise the move’s estimated travel minutes.',
      },
      {
        icon: <ClockIcon className={iconClass} aria-hidden="true" />,
        title: 'Location dwell takes priority',
        description: 'Use the destination location’s Dwell Time when it has a valid value, including zero. Otherwise use 90 minutes for live moves or 45 minutes for drops. This rule applies at each move’s destination before continuing to the next move. A location value replaces the default; it is not added on top of it.',
      },
      {
        icon: <LinkIcon className={iconClass} aria-hidden="true" />,
        title: 'Same-location pickups and linked legs',
        description: 'If the next origin is the previous destination, its arrival time is the same and travel between them is zero. Stop time is still added before departure toward the next destination, once at that shared stop. For linked legs on the same PRO, the main projection label shows the next destination arrival.',
      },
      {
        icon: <MapPinIcon className={iconClass} aria-hidden="true" />,
        title: 'When the driver has already arrived',
        description: 'Once GWI360 shows At Destination and the fresh route comparison confirms arrival, the projection starts from now. Remaining stop minutes = max(location dwell or default − minutes already at the stop, 0). Example: a 45-minute drop with 20 minutes already spent leaves 25 minutes before onward travel.',
      },
    ],
  },
  {
    title: 'Worked examples — all durations are minutes',
    items: [
      {
        icon: <CalculatorIcon className={iconClass} aria-hidden="true" />,
        title: 'Two live stops without location dwell values',
        description: '14:09 + 90 + 51 + 90 + 32 = 18:32. Arrive at BPT at 14:09, spend 90 minutes there, drive 51 minutes to SELECTUS (16:30), spend 90 minutes there, then drive 32 minutes to IMS (18:32). The next pickup at BPT still shows 14:09 because the driver is already arriving at that same location. 18:32 is arrival at IMS, before any stop time there.',
      },
      {
        icon: <ClockIcon className={iconClass} aria-hidden="true" />,
        title: 'A location override vs. the drop default',
        description: 'With a 25-minute location dwell: 14:09 arrival + 25 at the stop + 15 driving = 14:49 at the next origin. With no location dwell on a drop: 14:09 + 45 + 15 = 15:09. With an explicit zero dwell: 14:09 + 0 + 15 = 14:24. The following move’s own travel time is then added to calculate its destination arrival.',
      },
    ],
  },
  {
    title: 'Risk and travel totals',
    items: [
      {
        icon: <ExclamationTriangleIcon className={iconClass} aria-hidden="true" />,
        title: 'Appointment-risk buffer',
        description: 'Buffer = minutes until the deadline − cumulative estimated travel minutes in the original ETA cascade. On track: 30 minutes or more. At risk: 0 to under 30 minutes. Will miss: less than 0. Missed: the deadline has already passed. The deadline normally uses the end of the appointment window, or receiving hours (17:00 if unavailable); a multi-day pickup can use its origin deadline. LFD and per diem dates are not the appointment deadline.',
      },
      {
        icon: <TruckIcon className={iconClass} aria-hidden="true" />,
        title: 'Est. Travel and HOS',
        description: 'Est. Travel totals estimated travel for incomplete moves due today, plus travel between those moves. It does not include the Samsara projection’s dwell allowances. HOS clocks and workload warnings are shown separately; these GWI360 projections do not automatically schedule future driver breaks or HOS resets.',
      },
    ],
  },
  {
    title: 'Updates and when a projection is available',
    items: [
      {
        icon: <ArrowPathIcon className={iconClass} aria-hidden="true" />,
        title: 'Refresh timing and stale estimates',
        description: 'The visible monitor refreshes about every 30 seconds. Sync checks Samsara routes every five minutes; that check does not force Samsara to generate a new ETA. Temporary refresh failures keep the last successful result and its original check time. If that ETA passes between polls, it and its projections remain visible as “awaiting update”; this does not confirm arrival. A comparison older than ten minutes is stale and cannot supply a fresh projection. Samsara times display in the arrival location’s timezone. Hover over a comparison to see its source and last check; the arrow opens the source route in Samsara.',
      },
      {
        icon: <ExclamationTriangleIcon className={iconClass} aria-hidden="true" />,
        title: 'Move progress and missing inputs',
        description: 'GWI360 move status must agree with the route before its ETA can start a projection: GPS presence alone cannot start a queued move. A projection stops at missing travel information, incompatible move status, or a future-day gap; it does not invent a 30-minute travel fallback. To show comparisons, enable Application Preferences → Drivers → Show Samsara Route ETAs.',
      },
    ],
  },
];

/** Register content for the existing global information button on this page. */
export function useDriverStatusLegend() {
  const { setLegendSections } = useIconLegend();
  useEffect(() => {
    setLegendSections(sections, 'Driver Status Monitor — ETA Calculations');
    return () => setLegendSections([]);
  }, [setLegendSections]);
}
